import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "../maybe";
import {
  reorderChaptersExactSlot,
  reorderExactSlotByKey,
} from "../reorderExactSlot";
import { assertNever } from "../assertNever";
import { flattenChapters } from "./scenes";
import type { ManuscriptItem, ZwAct, ZwChapter, ZwMapFigure } from "./types";

/**
 * Stable outline / reorder key for a top-level manuscript item.
 * Chapters and acts use their folder; map leaves use their id.
 */
export function manuscriptItemKey(item: ManuscriptItem): string {
  switch (item.type) {
    case "chapter":
    case "act":
      return item.folder;
    case "map":
      return item.id;
    default:
      return assertNever(item);
  }
}

/** @deprecated Prefer `manuscriptItemKey` (maps have no folder). */
export function manuscriptItemFolder(item: ManuscriptItem): string {
  return manuscriptItemKey(item);
}

/** True when any act or chapter already claims `folder`. Acts and chapters share
 * one flat `Chapters/<folder>` namespace on disk, so uniqueness spans both.
 * Map leaves are not part of that namespace. */
export function manuscriptFolderExists(
  manuscript: ManuscriptItem[],
  folder: string,
): boolean {
  const target = folder.toLowerCase();
  return manuscript.some((item) => {
    if (item.type === "map") return false;
    if (item.folder.toLowerCase() === target) return true;
    return (
      item.type === "act" &&
      item.chapters.some((c) => c.folder.toLowerCase() === target)
    );
  });
}

export function newMapFigureId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `map-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `map-${Date.now().toString(36)}`;
}

export function findMapById(
  manuscript: ManuscriptItem[],
  id: string,
): Maybe<ZwMapFigure> {
  const itemM = toMaybe(
    manuscript.find((item) => item.type === "map" && item.id === id),
  );
  if (isNothing(itemM) || itemM.value.type !== "map") return nothing;
  return just(itemM.value);
}

export function replaceMapFigure(
  manuscript: ManuscriptItem[],
  id: string,
  next: ZwMapFigure,
): ManuscriptItem[] {
  return manuscript.map((item) =>
    item.type === "map" && item.id === id
      ? { type: "map" as const, ...next }
      : item,
  );
}

/** Insert a map figure leaf at a top-level index (clamped). */
export function insertMapFigure(
  manuscript: ManuscriptItem[],
  figure: ZwMapFigure,
  atIndex: number,
): ManuscriptItem[] {
  const next = [...manuscript];
  const idx = Math.max(0, Math.min(atIndex, next.length));
  next.splice(idx, 0, { type: "map", ...figure });
  return next;
}

/** Remove a map leaf by id. Does not delete the `.zwmap` file. */
export function removeMapFigure(
  manuscript: ManuscriptItem[],
  id: string,
): ManuscriptItem[] {
  return manuscript.filter((item) => item.type !== "map" || item.id !== id);
}

/** Reorder a map leaf among top-level manuscript items (exact-slot). */
export function moveMapBefore(
  manuscript: ManuscriptItem[],
  mapId: string,
  beforeKey: Maybe<string>,
): ManuscriptItem[] {
  return reorderExactSlotByKey(manuscript, manuscriptItemKey, mapId, beforeKey);
}

export function findActByFolder(
  manuscript: ManuscriptItem[],
  folder: string,
): Maybe<ZwAct> {
  const actM = toMaybe(
    manuscript.find((item) => item.type === "act" && item.folder === folder),
  );
  if (isNothing(actM) || actM.value.type !== "act") return nothing;
  return just(actM.value);
}

export function findChapterByFolder(
  manuscript: ManuscriptItem[],
  folder: string,
): Maybe<ZwChapter> {
  return toMaybe(flattenChapters(manuscript).find((c) => c.folder === folder));
}

/** The act a chapter sits in, or Nothing when it's ungrouped. */
export function actFolderForChapter(
  manuscript: ManuscriptItem[],
  chapterFolder: string,
): Maybe<string> {
  for (const item of manuscript) {
    if (item.type !== "act") continue;
    if (item.chapters.some((c) => c.folder === chapterFolder)) {
      return just(item.folder);
    }
  }
  return nothing;
}

/** Replaces one chapter wherever it sits (top level or inside an act), leaving
 * the surrounding structure untouched. */
export function replaceChapter(
  manuscript: ManuscriptItem[],
  folder: string,
  next: ZwChapter,
): ManuscriptItem[] {
  return manuscript.map((item) => {
    if (item.type === "chapter") {
      return item.folder === folder
        ? { type: "chapter" as const, ...next }
        : item;
    }
    if (item.type === "map") return item;
    if (item.chapters.some((c) => c.folder === folder) === false) return item;
    return {
      ...item,
      chapters: item.chapters.map((c) => (c.folder === folder ? next : c)),
    };
  });
}

/** Pulls a chapter out of the tree. Returns the removed chapter alongside the
 * remaining structure; an act emptied by the removal stays put (empty acts are a
 * valid state, not something to clean up behind the user). */
function detachChapter(
  manuscript: ManuscriptItem[],
  folder: string,
): { chapter: Maybe<ZwChapter>; rest: ManuscriptItem[] } {
  let chapter: Maybe<ZwChapter> = nothing;
  const rest: ManuscriptItem[] = [];
  for (const item of manuscript) {
    if (item.type === "chapter") {
      if (item.folder === folder) {
        const { type: _type, ...rec } = item;
        chapter = just(rec);
        continue;
      }
      rest.push(item);
      continue;
    }
    if (item.type === "map") {
      rest.push(item);
      continue;
    }
    const foundM = toMaybe(item.chapters.find((c) => c.folder === folder));
    if (isNothing(foundM)) {
      rest.push(item);
      continue;
    }
    chapter = just(foundM.value);
    rest.push({
      ...item,
      chapters: item.chapters.filter((c) => c.folder !== folder),
    });
  }
  return { chapter, rest };
}

/**
 * A single insertion slot in the manuscript tree. Nothing actFolder means the
 * top-level (ungrouped) list. Nothing beforeFolder means "append at the end" of
 * that list.
 *
 * Dropping onto a row means "take this row's exact slot", via
 * `reorderExactSlotByKey` / `reorderChaptersExactSlot` — never look up the
 * target after removing the dragged item.
 */
export type ManuscriptSlot = {
  actFolder: Maybe<string>;
  beforeFolder: Maybe<string>;
};

function slotActFolder(slot: ManuscriptSlot): Maybe<string> {
  return slot.actFolder;
}

function slotBeforeFolder(slot: ManuscriptSlot): Maybe<string> {
  return slot.beforeFolder;
}

/**
 * Moves a chapter to `slot`, taking it out of wherever it currently is. Handles
 * every case in the plan's drag table at once: reordering within the top level,
 * dropping onto an act (append as its last child), moving between an act's
 * chapters, and moving back out to the ungrouped list.
 */
export function moveChapterToSlot(
  manuscript: ManuscriptItem[],
  chapterFolder: string,
  slot: ManuscriptSlot,
): ManuscriptItem[] {
  const sourceAct = actFolderForChapter(manuscript, chapterFolder);

  const slotActM = slotActFolder(slot);
  const sameList =
    (isNothing(sourceAct) && isNothing(slotActM)) ||
    (isJust(sourceAct) &&
      isJust(slotActM) &&
      sourceAct.value === slotActM.value);

  if (sameList) {
    const actFolderM = slotActFolder(slot);
    if (isJust(actFolderM) && actFolderM.value !== "") {
      const actFolder = actFolderM.value;
      return manuscript.map((item) => {
        if (item.type !== "act" || item.folder !== actFolder) return item;
        return {
          ...item,
          chapters: reorderChaptersExactSlot(
            item.chapters,
            chapterFolder,
            slotBeforeFolder(slot),
          ),
        };
      });
    }
    return reorderExactSlotByKey(
      manuscript,
      manuscriptItemKey,
      chapterFolder,
      slotBeforeFolder(slot),
    );
  }

  const { chapter, rest } = detachChapter(manuscript, chapterFolder);
  if (isNothing(chapter)) return manuscript;

  const destActM = slotActFolder(slot);
  if (isJust(destActM) && destActM.value !== "") {
    const destAct = destActM.value;
    return rest.map((item) => {
      if (item.type !== "act" || item.folder !== destAct) return item;
      const beforeM = slotBeforeFolder(slot);
      const toIdx = matchMaybe(beforeM, {
        nothing: () => item.chapters.length,
        just: (beforeFolder) => {
          if (beforeFolder === "") return item.chapters.length;
          return item.chapters.findIndex((c) => c.folder === beforeFolder);
        },
      });
      if (isJust(beforeM) && beforeM.value !== "" && toIdx < 0) {
        return item;
      }
      const chapters = [...item.chapters];
      chapters.splice(toIdx, 0, chapter.value);
      return { ...item, chapters };
    });
  }

  const beforeM = slotBeforeFolder(slot);
  const toIdx = matchMaybe(beforeM, {
    nothing: () => rest.length,
    just: (beforeFolder) => {
      if (beforeFolder === "") return rest.length;
      return rest.findIndex((item) => manuscriptItemKey(item) === beforeFolder);
    },
  });
  if (isJust(beforeM) && beforeM.value !== "" && toIdx < 0) {
    return manuscript;
  }
  const next = [...rest];
  next.splice(Math.max(0, Math.min(toIdx, next.length)), 0, {
    type: "chapter",
    ...chapter.value,
  });
  return next;
}

/**
 * Moves an act (and every chapter nested under it, as one unit) so it occupies
 * the exact original slot of `beforeFolder`'s top-level item, or the end when
 * that's Nothing. Same helper chapters and scenes use.
 */
export function moveActBefore(
  manuscript: ManuscriptItem[],
  actFolder: string,
  beforeFolder: Maybe<string>,
): ManuscriptItem[] {
  return reorderExactSlotByKey(
    manuscript,
    manuscriptItemKey,
    actFolder,
    beforeFolder,
  );
}

/**
 * Where a newly created top-level item goes: immediately after the manuscript
 * span containing `afterFolder`. A chapter nested inside an act resolves to that
 * act's span, so a new act can never land inside one. Nothing appends.
 */
export function insertIndexAfterSpan(
  manuscript: ManuscriptItem[],
  afterFolder: Maybe<string>,
): number {
  return matchMaybe(afterFolder, {
    nothing: () => manuscript.length,
    just: (folder) => {
      if (folder === "") return manuscript.length;
      const idx = manuscript.findIndex(
        (item) =>
          manuscriptItemKey(item) === folder ||
          (item.type === "act" &&
            item.chapters.some((c) => c.folder === folder)),
      );
      return idx < 0 ? manuscript.length : idx + 1;
    },
  });
}

/** Change a display name without rebuilding scene arrays or changing identity. */
export function renameManuscriptItem(
  manuscript: ManuscriptItem[],
  folder: string,
  name: string,
): ManuscriptItem[] {
  return manuscript.map((item) => {
    switch (item.type) {
      case "map":
        return item;
      case "chapter":
        return item.folder === folder ? { ...item, name } : item;
      case "act":
        if (item.folder === folder) return { ...item, name };
        if (
          item.chapters.some((chapter) => chapter.folder === folder) === false
        )
          return item;
        return {
          ...item,
          chapters: item.chapters.map((chapter) =>
            chapter.folder === folder ? { ...chapter, name } : chapter,
          ),
        };
      default:
        return assertNever(item);
    }
  });
}
